import React, { useState, useEffect } from 'react';
import WelcomeCard from '../../components/common/WelcomeCard';
import { LiaBlogSolid } from "react-icons/lia";
import { FaEdit, FaTrash, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import axios from 'axios';
import AddServiceModal from '../../components/modals/AddServiceModal';
import EditServiceModal from '../../components/modals/EditServiceModal';
import Loader from '../../components/common/Loader';
import toast from 'react-hot-toast';

const Services = () => {
    const [serviceList, setServiceList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openAddModal, setOpenAddModal] = useState(false);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [selectedService, setSelectedService] = useState(null);

    // Search state
    const [searchTerm, setSearchTerm] = useState('');

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(8);

    const fetchServices = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/services/get-all-services`);
            const servicesData = Array.isArray(response.data) ? response.data : [];
            setServiceList(servicesData);
        } catch (err) {
            console.error(err);
            setError('Failed to load services');
            toast.error('Failed to load services');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchServices();
    }, []);

    const handleServiceAdded = (newService) => {
        setServiceList([newService, ...serviceList]);
    };

    const handleServiceUpdated = (updatedService) => {
        setServiceList(serviceList.map(s => (s?._id === updatedService?._id ? updatedService : s)));
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this service?')) {
            try {
                await axios.delete(`${process.env.REACT_APP_API_URL}/services/delete-service/${id}`);
                setServiceList(serviceList.filter(service => service?._id !== id));
                toast.success('Service deleted successfully');
            } catch (err) {
                console.error('Failed to delete service', err);
                toast.error('Failed to delete service');
            }
        }
    };

    const handleOpenAddModal = () => setOpenAddModal(true);
    const handleCloseAddModal = () => setOpenAddModal(false);

    const handleOpenEditModal = (service) => {
        setSelectedService(service);
        setOpenEditModal(true);
    };

    const handleCloseEditModal = () => setOpenEditModal(false);

    //  Filter services by search term
    const filteredServices = serviceList.filter(service =>
        service?.serviceName?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Pagination logic based on filtered results
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredServices.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredServices.length / itemsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);
    const nextPage = () => currentPage < totalPages && setCurrentPage(currentPage + 1);
    const prevPage = () => currentPage > 1 && setCurrentPage(currentPage - 1);

    if (error) {
        return <div className="text-center text-red-500 mt-10">{error}</div>;
    }

    return (
        <>
            {/* Add & Edit Modals */}
            <AddServiceModal
                isOpen={openAddModal}
                onClose={handleCloseAddModal}
                onServiceAdded={handleServiceAdded}
                allService={fetchServices}
            />

            {selectedService && (
                <EditServiceModal
                    isOpen={openEditModal}
                    onClose={handleCloseEditModal}
                    service={selectedService}
                    onServiceUpdated={handleServiceUpdated}
                    allService={fetchServices}
                />
            )}

            <div className='p-4'>
                <WelcomeCard />
                <div className='flex flex-col sm:flex-row justify-between items-center mb-4 gap-3'>
                    <h1 className="text-xl font-semibold text-[#000000] tracking-wider">
                        Services List
                    </h1>

                    <div className='flex items-center gap-2 w-full sm:w-auto'>
                        {/* Search Field with Icon */}
                        <div className='relative w-full sm:w-64'>
                            <span className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400'>
                                🔍
                            </span>
                            <input
                                type="text"
                                placeholder="Search services..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            />
                        </div>

                        {/* Add Service Button */}
                        <button
                            onClick={handleOpenAddModal}
                            className="flex justify-center items-center bg-gradient-to-r from-[#FF3131] to-[#ff9301] text-white font-semibold tracking-wider py-2 px-4 rounded-md hover:bg-red-600 transition duration-300 ease-in-out"
                        >
                            <LiaBlogSolid size={20} className='mr-2' /> Add Service
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className='rounded-md flex tracking-wider font-semibold justify-center items-center border border-slate-400 w-full p-4'>
                        <Loader /> <span className='ml-2'>Loading Services...</span>
                    </div>
                ) : filteredServices.length === 0 ? (
                    <div className='rounded-md flex tracking-wider font-semibold justify-center items-center border border-slate-400 w-full p-4'>
                        No services found.
                    </div>
                ) : (
                    <>
                        <div className="container mx-auto">
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                                {currentItems.map(service => (
                                    <div key={service?._id} className="bg-white shadow-md rounded-lg p-4 transition transform hover:scale-105 relative">
                                        {/* Service Image */}
                                        {service?.images?.length > 0 && (
                                            <div className="mb-3">
                                                <img
                                                    src={service.images[0]}
                                                    alt={service.serviceName}
                                                    className="w-full h-32 object-cover rounded-md mb-2"
                                                />
                                            </div>
                                        )}

                                        <h2 className="text-xl font-bold text-center mb-2">{service?.serviceName}</h2>

                                        <div className="flex justify-center space-x-4 mt-4">
                                            <button
                                                onClick={() => handleOpenEditModal(service)}
                                                className="text-blue-500 hover:text-blue-700"
                                                title="Edit Service"
                                            >
                                                <FaEdit size={20} />
                                            </button>

                                            <button
                                                onClick={() => handleDelete(service?._id)}
                                                className="text-red-500 hover:text-red-700"
                                                title="Delete Service"
                                            >
                                                <FaTrash size={20} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                            <div className="flex justify-center items-center mt-8 space-x-2">
                                <button
                                    onClick={prevPage}
                                    disabled={currentPage === 1}
                                    className={`p-2 rounded-md ${currentPage === 1
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-orange-500 text-white hover:bg-orange-600'
                                        }`}
                                >
                                    <FaChevronLeft />
                                </button>

                                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                    <button
                                        key={page}
                                        onClick={() => paginate(page)}
                                        className={`px-3 py-1 rounded-md ${currentPage === page
                                                ? 'bg-orange-500 text-white'
                                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                            }`}
                                    >
                                        {page}
                                    </button>
                                ))}

                                <button
                                    onClick={nextPage}
                                    disabled={currentPage === totalPages}
                                    className={`p-2 rounded-md ${currentPage === totalPages
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-orange-500 text-white hover:bg-orange-600'
                                        }`}
                                >
                                    <FaChevronRight />
                                </button>
                            </div>
                        )}

                        {/* Page Info */}
                        {filteredServices.length > 0 && (
                            <div className="text-center mt-4 text-gray-600">
                                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredServices.length)} of {filteredServices.length} services
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
};

export default Services;
